import SeoHead from "@/components/SeoHead";
import FeaturePageLayout from "@/components/FeaturePageLayout";
import TypewriterDemo from "@/components/TypewriterDemo";
import { Zap, MousePointer, Clock, Webhook, Mail, Eye, ShoppingBag, CreditCard, Send, Brain, CheckCircle, Target } from "lucide-react";
import { SITE_URL } from "@/lib/siteConfig";

const SCENARIOS = [
  {
    text: "Subscriber clicked pricing page link → Send demo invite",
    results: [
      { icon: <MousePointer size={14} className="text-blue-500" />, title: "Link click detected", desc: "subscriber@company.com clicked 'View Pricing' in Campaign #142" },
      { icon: <Clock size={14} className="text-amber-500" />, title: "24-hour delay started", desc: "Follow-up email queued for delivery tomorrow at subscriber's peak hour" },
      { icon: <Send size={14} className="text-coral" />, title: "Demo invite sent", desc: "Personalized email with calendar link and case study delivered" },
      { icon: <CheckCircle size={14} className="text-emerald-500" />, title: "Automation complete", desc: "Subscriber moved to 'Sales Pipeline' segment automatically" },
    ],
  },
  {
    text: "Shopify order webhook → Post-purchase thank you + upsell",
    results: [
      { icon: <Webhook size={14} className="text-purple-500" />, title: "Webhook received from Shopify", desc: "Order #4821 confirmed — $89.00 from returning customer" },
      { icon: <Mail size={14} className="text-blue-500" />, title: "Thank-you email sent", desc: "Branded order confirmation with cross-sell recommendations" },
      { icon: <Clock size={14} className="text-amber-500" />, title: "3-day upsell queued", desc: "Follow-up with complementary product suggestions scheduled" },
      { icon: <CheckCircle size={14} className="text-emerald-500" />, title: "Flow complete", desc: "Subscriber tagged 'repeat-buyer' and engagement score updated" },
    ],
  },
  {
    mistype: "No opens in 30",
    text: "Subscriber inactive 30+ days → Re-engagement campaign",
    results: [
      { icon: <Eye size={14} className="text-slate" />, title: "Inactivity detected", desc: "jane@example.com hasn't opened any email in 34 days" },
      { icon: <Send size={14} className="text-coral" />, title: "Win-back email sent", desc: '"We miss you" email with special 15% discount code delivered' },
      { icon: <CheckCircle size={14} className="text-emerald-500" />, title: "Monitoring response", desc: "If no open in 7 days, subscriber flagged for sunset review" },
    ],
  },
];

export default function BehavioralTriggersLandingPage() {
  return (
    <>
      <SeoHead
        title="Behavioral Email Triggers — React to Every Subscriber Action | MoreMail"
        description="Trigger automated emails when subscribers open, click, go inactive, or when external events fire from Shopify, Stripe, and Zapier."
        canonical={`${SITE_URL}/behavioral-triggers`}
        keywords="email behavioral triggers, event-driven email, webhook email automation, subscriber behavior triggers, automated email responses, Shopify email triggers"
        ogTitle="Behavioral Triggers — React to Every Subscriber Action | MoreMail"
        ogDescription="Trigger emails on opens, clicks, inactivity, and webhooks from Shopify, Stripe, and Zapier."
        jsonLd={{ "@context": "https://schema.org", "@type": "SoftwareApplication", name: "Behavioral Email Triggers", applicationCategory: "BusinessApplication", description: "Event-driven email automation.", offers: { "@type": "Offer", price: "0", priceCurrency: "USD" } }}
      />
      <FeaturePageLayout
        badge="Automation" badgeIcon={<Zap size={13} className="text-coral" />}
        heroTitle={<>React to Every<br /><em className="text-coral not-italic">Subscriber Behavior</em></>}
        heroSubtitle="Trigger automated emails when subscribers open, click, go inactive, or when external events fire from Shopify, Stripe, and Zapier."
        stats={[{ value: "9", label: "Trigger types" }, { value: "<1s", label: "Trigger latency" }, { value: "3x", label: "Higher engagement" }]}
        demo={<TypewriterDemo scenarios={SCENARIOS} title="Trigger Engine" buttonLabel="Trigger" />}
        howTitle="Set Triggers, Sit Back"
        howDesc="Configure once. Every matching event fires the automation instantly."
        howSteps={[
          { title: "Choose a Trigger", desc: "Pick from 9 trigger types: subscriber added, tag added, email opened, link clicked, campaign sent, reply received, inactivity, webhook received, or manual trigger." },
          { title: "Build the Response", desc: "Send an email, apply a tag, wait a delay, add a condition, fire a webhook — chain multiple actions into a complete workflow with branching logic." },
          { title: "Activate and Monitor", desc: "Turn it on and it runs 24/7. View trigger logs to see every event and the actions that fired." },
        ]}
        benefitsTitle="Why Behavioral Triggers Outperform Scheduled Emails"
        benefits={[
          { icon: <MousePointer size={18} className="text-coral" />, title: "Link Click Follow-ups", desc: "When someone clicks your pricing page, send a personalized demo invite 24 hours later." },
          { icon: <Clock size={18} className="text-coral" />, title: "Inactivity Win-Backs", desc: "Set inactivity from 1–365 days. Subscribers who exceed the threshold trigger your win-back flow." },
          { icon: <ShoppingBag size={18} className="text-coral" />, title: "Post-Purchase Flows", desc: "Shopify fires an order webhook. MoreMail instantly sends thank-you emails and cross-sell sequences." },
          { icon: <Eye size={18} className="text-coral" />, title: "Interest-Based Nurturing", desc: "When someone opens your product announcement, follow up with a detailed feature breakdown." },
          { icon: <Mail size={18} className="text-coral" />, title: "Multi-Touch Campaigns", desc: "After a launch email, auto-queue a reminder in 3 days and a last-call in 5 days." },
          { icon: <CreditCard size={18} className="text-coral" />, title: "Payment Upsells", desc: "When Stripe confirms a payment, trigger an upsell sequence for premium features." },
        ]}
        ctaHeadline="The Right Email at the Right Moment"
        ctaDescription="Behavioral triggers turn your email from broadcast to conversation. Set them up once and they work forever."
        currentPath="/behavioral-triggers"
      >
        {/* All 9 trigger types + flow step types */}
        <section className="py-12 bg-white">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="font-display text-2xl font-bold text-ink text-center mb-3">
              9 Trigger Types, <span className="text-coral">Unlimited Flows</span>
            </h2>
            <p className="text-sm text-slate text-center mb-8 max-w-lg mx-auto">
              Every subscriber action can start an automation. Combine triggers with conditions, delays, and actions to build any workflow.
            </p>
            <div className="grid md:grid-cols-3 gap-3 mb-8">
              {[
                { label: "Subscriber Added", desc: "New subscriber joins your list" },
                { label: "Tag Added", desc: "A tag is applied to a subscriber" },
                { label: "Email Opened", desc: "Subscriber opens a specific email" },
                { label: "Link Clicked", desc: "Subscriber clicks a link in an email" },
                { label: "Campaign Sent", desc: "A campaign finishes sending" },
                { label: "Reply Received", desc: "Subscriber replies to an email" },
                { label: "Inactivity", desc: "No opens for N days (1–365)" },
                { label: "Webhook Received", desc: "External event from Shopify, Stripe, Zapier" },
                { label: "Manual Trigger", desc: "You trigger it yourself via the UI or API" },
              ].map(t => (
                <div key={t.label} className="bg-paper rounded-xl p-3 border border-sand text-center">
                  <p className="text-xs font-bold text-ink">{t.label}</p>
                  <p className="text-[10px] text-slate mt-0.5">{t.desc}</p>
                </div>
              ))}
            </div>
            <h3 className="font-display font-bold text-ink text-sm text-center mb-4">Flow Step Types</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { label: "Send Email", desc: "Deliver a campaign" },
                { label: "Wait / Delay", desc: "Pause N hours or days" },
                { label: "Condition", desc: "Branch on tags, opens, status" },
                { label: "Apply Tag", desc: "Add a tag to subscriber" },
                { label: "Fire Webhook", desc: "Call an external URL" },
              ].map(s => (
                <div key={s.label} className="bg-paper rounded-xl p-3 border border-sand text-center">
                  <p className="text-xs font-bold text-ink">{s.label}</p>
                  <p className="text-[10px] text-slate mt-0.5">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </FeaturePageLayout>
    </>
  );
}
